/**
 * SoleadLab 2.0 — Batch Move Geo Photos
 *
 * Lets the user drag all geo-photo indications as a group.
 * Alt/Ctrl+click to exclude individual photos from the batch.
 * Enter = confirm, Escape = revert.
 *
 * REFERENCE: editor (23).html lines 11442-11699
 */

import { useEffect, useRef, useCallback } from 'react';
import type { MapMouseEvent } from 'mapbox-gl';
import { useMapStore, useProjectStore, useUIStore } from '../../stores';
import { reapplyTopologyForIndications } from '../../domain/topology/reapplyTopology';

// ── Geometry translation helper ──────────────────────────────────────

function translateGeometry(
  geom: GeoJSON.Geometry,
  deltaLng: number,
  deltaLat: number,
): GeoJSON.Geometry {
  if (geom.type === 'Polygon') {
    return {
      type: 'Polygon',
      coordinates: (geom as GeoJSON.Polygon).coordinates.map((ring) =>
        ring.map(([lng, lat]) => [lng + deltaLng, lat + deltaLat]),
      ),
    };
  }
  if (geom.type === 'Point') {
    const pt = geom as GeoJSON.Point;
    return {
      type: 'Point',
      coordinates: [pt.coordinates[0] + deltaLng, pt.coordinates[1] + deltaLat],
    };
  }
  return geom;
}

// ── Draw layer IDs we style for highlighting ─────────────────────────

const FILL_LAYERS = [
  'gl-draw-polygon-fill-active',
  'gl-draw-polygon-fill-inactive',
];
const LINE_LAYERS = [
  'gl-draw-polygon-stroke-active',
  'gl-draw-polygon-stroke-inactive',
];

// ── Hook ─────────────────────────────────────────────────────────────

export function useBatchMove() {
  const map = useMapStore((s) => s.map);
  const draw = useMapStore((s) => s.draw);

  // Refs for drag state (no re-renders during drag)
  const activeRef = useRef(false);
  const draggingRef = useRef(false);
  const startLngLatRef = useRef<{ lng: number; lat: number } | null>(null);
  const featureIdsRef = useRef<string[]>([]);
  const excludedRef = useRef<Set<string>>(new Set());
  const originalGeomsRef = useRef<Record<string, GeoJSON.Geometry>>({});

  // ── Helpers ──────────────────────────────────────────────────────

  const getGeoPhotoIds = useCallback((): string[] => {
    if (!draw) return [];
    const all = draw.getAll();
    const ids: string[] = [];
    for (const f of all.features || []) {
      // Match all Photo-type indications regardless of image URL format
      if (f.properties?.ind_type === 'Photo') {
        ids.push(f.id as string);
      }
    }
    return ids;
  }, [draw]);

  const highlightFeatures = useCallback(() => {
    if (!map) return;
    const included = featureIdsRef.current.filter((id) => !excludedRef.current.has(id));
    const excluded = [...excludedRef.current];

    for (const layerId of FILL_LAYERS) {
      try {
        map.setPaintProperty(layerId, 'fill-color', [
          'case',
          ['in', ['get', 'id'], ['literal', included]], '#f97316',
          ['in', ['get', 'id'], ['literal', excluded]], '#9ca3af',
          ['coalesce', ['get', 'user_color'], '#3bb2d0'],
        ]);
        map.setPaintProperty(layerId, 'fill-opacity', [
          'case',
          ['in', ['get', 'id'], ['literal', included]], 0.6,
          ['in', ['get', 'id'], ['literal', excluded]], 0.2,
          0.4,
        ]);
      } catch { /* layer may not exist yet */ }
    }

    for (const layerId of LINE_LAYERS) {
      try {
        map.setPaintProperty(layerId, 'line-color', [
          'case',
          ['in', ['get', 'id'], ['literal', included]], '#ea580c',
          ['in', ['get', 'id'], ['literal', excluded]], '#6b7280',
          '#000000',
        ]);
        map.setPaintProperty(layerId, 'line-width', [
          'case',
          ['in', ['get', 'id'], ['literal', included]], 3,
          ['in', ['get', 'id'], ['literal', excluded]], 1,
          1.5,
        ]);
      } catch { /* */ }
    }
  }, [map]);

  const resetDrawStyles = useCallback(() => {
    if (!map) return;
    for (const layerId of FILL_LAYERS) {
      try {
        map.setPaintProperty(layerId, 'fill-color', ['coalesce', ['get', 'user_color'], layerId.includes('active') ? '#fbb03b' : '#3bb2d0']);
        map.setPaintProperty(layerId, 'fill-opacity', 0.4);
      } catch { /* */ }
    }
    for (const layerId of LINE_LAYERS) {
      try {
        map.setPaintProperty(layerId, 'line-color', '#000000');
        map.setPaintProperty(layerId, 'line-width', layerId.includes('active') ? 2 : 1.5);
      } catch { /* */ }
    }
  }, [map]);

  // ── Core actions ────────────────────────────────────────────────

  const start = useCallback(() => {
    if (!map || !draw) return;
    const ids = getGeoPhotoIds();
    if (ids.length === 0) {
      useUIStore.getState().addNotification({
        type: 'warning',
        title: 'No geo photos',
        message: 'Upload geo photos first',
      });
      return;
    }

    activeRef.current = true;
    featureIdsRef.current = ids;
    excludedRef.current = new Set();
    originalGeomsRef.current = {};
    draggingRef.current = false;
    startLngLatRef.current = null;

    // Store original geometries for revert
    for (const id of ids) {
      const f = draw.get(id);
      if (f) {
        originalGeomsRef.current[id] = JSON.parse(JSON.stringify(f.geometry));
      }
    }

    // Deselect all so draw doesn't interfere
    draw.changeMode('simple_select', { featureIds: [] });

    map.getCanvas().style.cursor = 'move';
    highlightFeatures();
    useMapStore.getState().setBatchMoveState(true, ids.length);
  }, [map, draw, getGeoPhotoIds, highlightFeatures]);

  const cancel = useCallback(() => {
    if (!map || !draw || !activeRef.current) return;

    // Revert to original positions — draw-layer only. The store was never
    // touched during the drag, so an abandoned move writes NOTHING and
    // derives NOTHING (cancel is inert by contract: the old syncDrawToStore
    // here re-derived topology for every feature on the map, firing writes
    // on faults the user never selected, on a gesture they abandoned).
    for (const [id, geom] of Object.entries(originalGeomsRef.current)) {
      const f = draw.get(id);
      if (f) {
        f.geometry = geom;
        draw.delete(id);
        draw.add(f);
      }
    }

    activeRef.current = false;
    featureIdsRef.current = [];
    excludedRef.current = new Set();
    originalGeomsRef.current = {};
    draggingRef.current = false;
    startLngLatRef.current = null;

    map.getCanvas().style.cursor = '';
    resetDrawStyles();
    useMapStore.getState().setBatchMoveState(false, 0);
  }, [map, draw, resetDrawStyles]);

  const finish = useCallback(() => {
    if (!map || !activeRef.current) return;

    // The MOVED set = the batch minus exclusions — captured BEFORE the refs
    // are cleared (the old code cleared first, lost the selection, and then
    // synced+re-derived every feature in the draw layer as a workaround).
    const movedIds = featureIdsRef.current.filter((id) => !excludedRef.current.has(id));

    activeRef.current = false;
    featureIdsRef.current = [];
    excludedRef.current = new Set();
    originalGeomsRef.current = {};
    draggingRef.current = false;
    startLngLatRef.current = null;

    map.getCanvas().style.cursor = '';
    resetDrawStyles();
    useMapStore.getState().setBatchMoveState(false, 0);

    syncMovedToStore(movedIds);
  }, [map, resetDrawStyles]);

  // Persist ONLY the moved features, then one batched topology pass (one
  // keep-or-update dialog per gesture, not per indication).
  const syncMovedToStore = useCallback((movedIds: string[]) => {
    if (!draw || movedIds.length === 0) return;
    for (const id of movedIds) {
      const f = draw.get(id);
      if (f?.geometry) {
        useProjectStore.getState().updateIndication(id, { geometry: f.geometry });
      }
    }
    reapplyTopologyForIndications(movedIds);
  }, [draw]);

  // ── Mouse handlers ──────────────────────────────────────────────

  const handleMouseDown = useCallback(
    (e: MapMouseEvent) => {
      if (!activeRef.current || !map || !draw) return;

      // Alt/Ctrl+click = toggle exclude
      if (e.originalEvent.altKey || e.originalEvent.ctrlKey) {
        const feats = map.queryRenderedFeatures(e.point);
        for (const feat of feats) {
          const id = (feat.properties?.id as string) || (feat.id as string);
          if (id && featureIdsRef.current.includes(id)) {
            if (excludedRef.current.has(id)) {
              excludedRef.current.delete(id);
            } else {
              excludedRef.current.add(id);
            }
            highlightFeatures();
            const count = featureIdsRef.current.length - excludedRef.current.size;
            useMapStore.getState().setBatchMoveState(true, count);
            e.preventDefault();
            return;
          }
        }
        return;
      }

      // Start drag
      startLngLatRef.current = { lng: e.lngLat.lng, lat: e.lngLat.lat };
      draggingRef.current = true;

      // Snapshot current positions as base for this drag
      originalGeomsRef.current = {};
      for (const id of featureIdsRef.current) {
        const f = draw.get(id);
        if (f) {
          originalGeomsRef.current[id] = JSON.parse(JSON.stringify(f.geometry));
        }
      }

      map.getCanvas().style.cursor = 'grabbing';
      map.dragPan.disable();
    },
    [map, draw, highlightFeatures],
  );

  const handleMouseMove = useCallback(
    (e: MapMouseEvent) => {
      if (!activeRef.current || !draggingRef.current || !startLngLatRef.current || !draw) return;

      const deltaLng = e.lngLat.lng - startLngLatRef.current.lng;
      const deltaLat = e.lngLat.lat - startLngLatRef.current.lat;

      for (const id of featureIdsRef.current) {
        if (excludedRef.current.has(id)) continue;
        const origGeom = originalGeomsRef.current[id];
        if (!origGeom) continue;

        const f = draw.get(id);
        if (!f) continue;

        f.geometry = translateGeometry(origGeom, deltaLng, deltaLat);
        draw.delete(id);
        draw.add(f);
      }
    },
    [draw],
  );

  const handleMouseUp = useCallback(
    (_e: MapMouseEvent) => {
      if (!activeRef.current || !draggingRef.current || !map) return;

      draggingRef.current = false;
      startLngLatRef.current = null;
      map.getCanvas().style.cursor = 'move';
      map.dragPan.enable();
    },
    [map],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!activeRef.current) return;
      if (e.key === 'Escape') {
        cancel();
        e.preventDefault();
      } else if (e.key === 'Enter') {
        finish();
        e.preventDefault();
      }
    },
    [cancel, finish],
  );

  // ── Attach/detach event listeners ───────────────────────────────

  useEffect(() => {
    if (!map) return;

    const onMouseDown = (e: MapMouseEvent) => handleMouseDown(e);
    const onMouseMove = (e: MapMouseEvent) => handleMouseMove(e);
    const onMouseUp = (e: MapMouseEvent) => handleMouseUp(e);

    map.on('mousedown', onMouseDown);
    map.on('mousemove', onMouseMove);
    map.on('mouseup', onMouseUp);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      map.off('mousedown', onMouseDown);
      map.off('mousemove', onMouseMove);
      map.off('mouseup', onMouseUp);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [map, handleMouseDown, handleMouseMove, handleMouseUp, handleKeyDown]);

  // ── Register callbacks in store so EditorPanel can call them ─────

  useEffect(() => {
    const store = useMapStore.getState();
    (store as any)._batchMoveStart = start;
    (store as any)._batchMoveFinish = finish;
    (store as any)._batchMoveCancel = cancel;
    return () => {
      const s = useMapStore.getState();
      (s as any)._batchMoveStart = undefined;
      (s as any)._batchMoveFinish = undefined;
      (s as any)._batchMoveCancel = undefined;
    };
  }, [start, finish, cancel]);
}
